import React from 'react';
import { AVATAR_SIZE_MEDIUM, type AvatarStatus } from './Avatar.constants';
import { Wrapper, InitialsText, StatusDot } from './Avatar.styled';
import { deriveInitials } from './deriveInitials';

export interface AvatarProps {
  /** Override the auto-derived initials. Capped to 2 chars at render time. */
  initials?: string;
  /** Used to derive initials when `initials` is omitted, and to label the button. */
  name?: string;
  size?: number;
  status?: AvatarStatus;
  /** Render as a `<button>` to make the avatar a click target (e.g. menu trigger). */
  as?: 'div' | 'button';
  type?: 'button' | 'submit' | 'reset';
  /**
   * Click handler — typed against `HTMLElement` because the avatar can render
   * as a button or a non-interactive div via the `as` prop.
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  ariaLabel?: string;
  ariaHasPopup?: 'menu' | 'dialog' | 'listbox' | 'tree' | 'grid' | 'true' | 'false';
  ariaExpanded?: boolean;
  /** When true, draws a permanent focus-style ring (e.g. while a menu is open). */
  isActive?: boolean;
  dataTestId?: string;
}

/**
 * Reusable circular avatar that displays a user's initials with an optional
 * status dot. Renders as a div by default; pass `as="button"` to make it
 * interactive (used by the ProfileMenu trigger).
 */
const Avatar = React.forwardRef<HTMLElement, AvatarProps>(function Avatar(
  {
    initials,
    name,
    size = AVATAR_SIZE_MEDIUM,
    status,
    as = 'div',
    onClick,
    ariaLabel,
    ariaHasPopup,
    ariaExpanded,
    isActive = false,
    dataTestId = 'avatar',
    type,
  },
  ref,
) {
  const resolvedInitials = (initials || deriveInitials(name)).slice(0, 2);
  const isButton = as === 'button';

  // `Wrapper` is `styled.div` and its prop types reflect that, but the runtime
  // tag swaps to `<button>` when `as="button"`. We extend the host props loosely
  // so we can splat button-specific attributes through without TS complaining.
  const buttonProps: Record<string, unknown> = isButton
    ? {
        type: type ?? 'button',
        onClick,
        'aria-label': ariaLabel || (name ? `Account — ${name}` : 'Account'),
        'aria-haspopup': ariaHasPopup,
        'aria-expanded': ariaExpanded,
      }
    : {};

  return (
    <Wrapper
      ref={ref as React.Ref<HTMLDivElement>}
      as={as}
      $size={size}
      $isInteractive={isButton}
      $isActive={isActive}
      data-testid={dataTestId}
      {...buttonProps}
    >
      <InitialsText $size={size}>{resolvedInitials}</InitialsText>
      {status && (
        <StatusDot
          $status={status}
          aria-hidden="true"
          data-testid={`${dataTestId}__status`}
        />
      )}
    </Wrapper>
  );
});

export default Avatar;
